import React from 'react';
import { FormControl } from './FormControl';
import { debounce, isEqual } from 'lodash';
import {
  autoComplete,
  checkSelectError,
  defaultMultiselectProps,
  getDefaultOptionLabel,
  getDefaultOptionValue,
  getFormattedValue,
  getSelectData,
  multiSelectPropTypes,
  onFocus,
  rendererProps,
  selectOnChange,
  translationProps,
} from '../ControlHelpers/SelectUtils.js';
import { selectControlStyles, selectTheme } from './SelectControl.styles';
import AsyncCreatable from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { selectComponents } from './SelectComponentWrappers';

export class SelectAsyncControl extends FormControl {
  static propTypes = multiSelectPropTypes;
  static defaultProps = defaultMultiselectProps;

  constructor(props) {
    super(props);

    const value = props.initialData;

    if (value && !value.display && typeof value === 'object') {
      if (props.buildDisplay) {
        value.display = props.buildDisplay(value);
      } else {
        value.display = value[props.displayProperty];
      }
    }

    this.state = {
      isLoading: false,
      options: [],
      value,
      isMenuOpen: false,
    };

    this.debouncedGetData = debounce(this.getData, props.valueChangeDelay, { leading: false, trailing: true });

    this.select = React.createRef();
  }

  componentDidMount() {
    const { value } = this.state;
    if (value && value.display) {
      this.onChange(value);
    }
  }

  componentDidUpdate = prevProps => {
    const { dataUrl, dataValue, filters, valueOverride, isLegacyOverride } = this.props;

    if (!isEqual(filters, filters) || prevProps.dataUrl !== dataUrl) {
      this.onChange();
    }

    if (valueOverride && dataValue !== this.state.value) {
      this.onChange(dataValue, isLegacyOverride);
    }
  };

  onChange = (val, skipValueChange) => {
    // react-select would return a metadata object sometimes as this method is used directly on it's onChange prop (https://react-select.com/props#:~:text=void-,onChange,-Handle%20change%20events)
    // we don't want that, only boolean values
    const skip = typeof skipValueChange === 'boolean' && skipValueChange;
    const value = selectOnChange(val, this.props, this.select?.current, skip);
    this.setState(prevState => ({ ...prevState, value }));
  };

  getData = async (input, callback) => {
    this.setState({ isLoading: true });

    const { appendOptions, clientSideFilter, defaultOptions, disabledOptionsIds } = this.props;
    const options = await getSelectData(input, this.props, clientSideFilter);
    let optionsArr = options?.map(({ id, data, ...props }) => ({ ...props, ...data, value: id }));

    if (appendOptions) {
      optionsArr.push(...appendOptions);
    }

    if (defaultOptions) {
      optionsArr.unshift(...defaultOptions);
    }

    if (disabledOptionsIds?.length) {
      optionsArr = optionsArr.filter(option => !disabledOptionsIds.includes(option.id));
    }

    /*
     * We need this here due to a current bug in react-select v5, which causes that if an options
     * property is part of the returned entities, the library thinks we want to create groups.
     * They have not fixed it yet but are aware of it: https://github.com/JedWatson/react-select/issues/3706
     * Here is the check: https://github.com/JedWatson/react-select/blob/c22a4b20aef2161ebaa3a0373de7e0e179e73693/packages/react-select/src/Select.js#L1315
     */
    optionsArr.forEach(opt => {
      if (opt.options) {
        /* some of our data objects, namely Templates in Notifi, have an attribute called options, that we need, so this renames them as well as deleting them. */
        opt.optionsAttr = opt.options;
      }
      delete opt.options;
    });

    this.setState({ isLoading: false, options: optionsArr });

    callback(optionsArr);
  };

  onFocus = e => {
    onFocus(e, this.props);
  };

  checkAndLoadOptions = (input, callback) => {
    if (!input && this.props.requireInput) {
      return Promise.resolve({ options: [] });
    } else {
      this.debouncedGetData(input, callback);
    }
  };

  onBlur = () => {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(this.state.value);
    }
    this.checkError();
  };

  isValid = () => {
    return this.input.validity.valid;
  };

  checkError() {
    checkSelectError(this.props, this.select?.current);
  }

  handleOnMenuOpen = () => this.setState({ isMenuOpen: true });

  handleOnMenuClose = () => this.setState({ isMenuOpen: false });

  handleOnKeyDown = event => {
    if (event.key === 'Enter' && !this.state.isMenuOpen) {
      this.handleOnMenuOpen();
    }
  };

  render() {
    const {
      className,
      cacheEnabled,
      clearable,
      creatable,
      disabled,
      isClearable,
      isDisabled,
      isMulti,
      isSearchable,
      multi,
      placeholder,
      placeholderText,
      searchable,
      ...props
    } = this.props;
    const { isLoading, options, value } = this.state;
    const isClearableSelect = !(clearable === false || isClearable === false);
    const isDisabledSelect = disabled || isDisabled || false;
    const isMultiSelect = multi || isMulti || false;
    const formattedName = isMultiSelect ? props.name + '[]' : props.name;
    const isSearchableSelect = searchable || isSearchable;
    const SelectedComponent = creatable ? AsyncCreatable : AsyncSelect;
    const classPrefix = `Select${props.name ? `-${props.name}` : ''}`;

    const Option = props => (
      // eslint-disable-next-line react/prop-types
      <div ref={props.innerRef} {...props.innerProps} {...props}>
        <selectComponents.Option {...props} helperText={props.data?.helperText ? props.data.helperText : null} />
      </div>
    );
    const components = {
      ...selectComponents,
      Option,
    };

    return (
      <SelectedComponent
        {...props}
        {...rendererProps(isMultiSelect, this.props)}
        {...translationProps}
        appendOptions
        aria-labelledby={`${props.name}-label`}
        cacheOptions={cacheEnabled}
        className={className ?? classPrefix}
        classNamePrefix="Select-"
        closeMenuOnSelect={!isMultiSelect}
        components={components}
        defaultOptions
        getOptionLabel={option => getDefaultOptionLabel(option, this.props)}
        getOptionValue={option => getDefaultOptionValue(option, this.props)}
        inputId={`${classPrefix}-input`}
        inputProps={autoComplete(this.props)}
        instanceId={`${props.name ? `${props.name}-` : ''}value`}
        isClearable={isClearableSelect}
        isDisabled={isDisabledSelect}
        isLoading={isLoading}
        isMulti={isMultiSelect}
        isSearchable={isSearchableSelect}
        labelKey="display"
        loadOptions={this.checkAndLoadOptions}
        name={formattedName}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onOpen={this.onOpen}
        placeholder={placeholderText || placeholder}
        ref={this.select}
        removeSelected={false}
        scrollMenuIntoView={false}
        styles={selectControlStyles({ isMultiSelect, value })}
        theme={selectTheme}
        value={getFormattedValue(value, options, this.props)}
        valueKey={props.valueKey ?? props.valueProperty}
        hideSelectedOptions={false}
        onMenuOpen={this.handleOnMenuOpen}
        onMenuClose={this.handleOnMenuClose}
        menuIsOpen={this.state.isMenuOpen}
        onKeyDown={this.handleOnKeyDown}
      />
    );
  }
}
